<template>
  <div id="trace-map" class="map-container"></div>
</template>

<script>
// Vue 2
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-polylinedecorator"; // 화살표 데코레이터

// ✅ 기본 아이콘 경로 (안 하면 아이콘이 안 보일 수 있음)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl:       require("leaflet/dist/images/marker-icon.png"),
  shadowUrl:     require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "mapArrow",
  props: {
    /**
     * points: [{ lat: number, lng: number, label?: string }]
     * 예: [{lat:37.57,lng:126.98,label:'제조'}, ...]
     */
    points: { type: Array, required: true },
    zoom: { type: Number, default: 6 },
    fitBounds: { type: Boolean, default: true },
    showSegmentDistance: { type: Boolean, default: true },
    draggable: { type: Boolean, default: false },
  },
  data() {
    return {
      map: null,
      markers: [],
      pathLine: null,
      arrowDecorator: null,
      segmentLabels: [],
      localPoints: [], // ✅ props의 복제본으로만 렌더링/수정
    };
  },
  mounted() {
    this.localPoints = (this.points || []).map(p => ({ ...p })); // 초기 동기화
    this.initMap();
    this.renderTrace();
  },
  beforeDestroy() {
    if (this.map) this.map.remove();
  },
  watch: {
    points: {
      deep: true,
      handler(newVal) {
        this.localPoints = (newVal || []).map(p => ({ ...p })); // 외부 변경 반영
        this.renderTrace();
      }
    }
  },
  methods: {
    initMap() {
      this.map = L.map("trace-map", { 
        zoomControl: true,
        attributionControl: false
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19
      }).addTo(this.map);
      L.control.attribution({ prefix: false }).addTo(this.map);
      this.map.setView([36.5, 127.8], this.zoom);
    },
    renderTrace() {
      // 기존 레이어/마커 정리
      this.markers.forEach(m => m.remove());
      this.markers = [];
      if (this.pathLine) { this.pathLine.remove(); this.pathLine = null; }
      if (this.arrowDecorator) { this.arrowDecorator.remove(); this.arrowDecorator = null; }
      this.segmentLabels.forEach(l => l.remove());
      this.segmentLabels = [];

      const pts = this.localPoints || [];

      if (!pts.length) return;

      const allValid = pts.every(p => ('lat' in p) && ('lng' in p));
      if (!allValid) {
        console.warn("좌표값이 없는 포인트가 있어서 선 그리기 스킵");
        return; // 선, 마커 아무것도 안 그림
      }

      const latlngs = pts.map(p => L.latLng(p.lat, p.lng));

      // 마커 + 라벨
      latlngs.forEach((ll, idx) => {
      const n = idx + 1;
      const p = pts[idx];
      const labelText = p.label || `Step ${n}`;

      const numbered = L.divIcon({
        className: "numbered-marker",
        html: `<div class="bubble">${n}</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
      });

      const marker = L.marker(ll, { icon: numbered, draggable: this.draggable })
        .addTo(this.map);

      const imgUrl = pts[idx].imageUrl;
      const imgHtml = imgUrl
        ? `<img src="${imgUrl}" class="thumb" />`
        : "";

      marker.bindTooltip(
        `
        <div class="point-tooltip">
          ${imgHtml}
          <div class="text">
            <div class="coords">${pts[idx].stepName} (${pts[idx].bizLocationName})</div>
            <div class="label">${labelText}</div>
          </div>
        </div>
        `,
        {
          permanent: true,
          direction: "top",
          className: "point-label"
        }
      );

      this.markers.push(marker);
    });

      // 경로 및 화살표
      if (latlngs.length >= 2) {
       this.pathLine = L.polyline(latlngs, {
          color: "#2563eb",
          weight: 4,
          opacity: 0.95,
          dashArray: "6, 6"   // ✅ 점선 스타일 추가
        }).addTo(this.map);

        // 화살표(방향 표시)
        this.arrowDecorator = L.polylineDecorator(this.pathLine, {
          patterns: [
            {
              offset: 20,
              repeat: 80,
              symbol: L.Symbol.arrowHead({
                pixelSize: 14,   // ✅ 크기 키움
                polygon: true,
                pathOptions: {
                  color: "#1e40af",   // ✅ 좀 더 진한 파랑 (대비 ↑)
                  fillOpacity: 0.9,
                  weight: 1
                }
              })
            }
          ]
        }).addTo(this.map);

        if (this.fitBounds) {
          const bounds = L.latLngBounds(latlngs);
          this.map.fitBounds(bounds, { padding: [40, 40] });
        }
      } else {
        // 포인트 1개인 경우 그 포인트로 센터링
        this.map.setView(latlngs[0], this.zoom);
      }
    },

    onMarkerDragEnd(idx, marker) {
      const pos = marker.getLatLng();

      if(pos.lat){
        // ✅ 불변 업데이트
        const newPoints = this.localPoints.map((p, i) =>
          i === idx ? { ...p, lat: pos.lat, lng: pos.lng } : p
        );

        this.localPoints = newPoints;                 // 내부 상태 갱신
        this.$emit("update:points", newPoints);       // v-model:points 패턴 지원
        this.$emit("update-point", { index: idx, lat: pos.lat, lng: pos.lng }); // 선택 이벤트

        this.renderTrace();
      }
    },
  },
};
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 60vh;
}

/* 번호 마커(작은 동그란 뱃지) */
.numbered-marker.leaflet-div-icon {
  background: transparent;
  border: none;
}
.bubble {
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  line-height: 24px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0,0,0,.25);
  transform: translateY(-6px);
}

/* 고정 툴팁(포인트 라벨) */
.point-label.leaflet-tooltip {
  background: rgba(17,24,39,.9);
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
}

/* 구간 거리칩 라벨 */
.distance-label.leaflet-div-icon {
  background: transparent;
  border: 0;
  width: auto !important;
  height: auto !important;
}
.distance-chip {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: rgba(17, 24, 39, 0.85);
  border-radius: 9999px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
  transform: translate(-50%, -10px);
}

.point-label.leaflet-tooltip {
  background: rgba(17,24,39,.9);
  border: 0;
  border-radius: 8px;
  padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
}

::v-deep .point-tooltip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;  
}

::v-deep .point-tooltip img.thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
}

::v-deep .point-tooltip .text {
  display: flex;
  flex-direction: column;
}

::v-deep .point-tooltip .label {
  font-weight: 600;
  font-size: 13px;
  color: #344767;
}

::v-deep .point-tooltip .coords {
  font-size: 11px;
  color: #344767;
}

</style>